class CurrencyConverter {
  constructor() {
    this.baseCurrency = 'BRL';
    this.rates = null;
    this.apiUrl = 'https://api.exchangerate-api.com/v4/latest/BRL';
    this.cacheKey = 'currency_rates_cache';
    this.cacheTimeKey = 'currency_rates_timestamp';
    this.cacheDuration = 24 * 60 * 60 * 1000; // 24 hours

    this.currencyMap = {
      'AE': { c: 'AED', s: 'د.إ' }, 'AR': { c: 'ARS', s: '$' }, 'AU': { c: 'AUD', s: '$' }, 
      'BR': { c: 'BRL', s: 'R$' }, 'CA': { c: 'CAD', s: '$' }, 'CH': { c: 'CHF', s: 'CHF' },
      'CL': { c: 'CLP', s: '$' }, 'CN': { c: 'CNY', s: '¥' }, 'CO': { c: 'COP', s: '$' },
      'DK': { c: 'DKK', s: 'kr.' }, 'GB': { c: 'GBP', s: '£' }, 'HK': { c: 'HKD', s: '$' },
      'ID': { c: 'IDR', s: 'Rp' }, 'IL': { c: 'ILS', s: '₪' }, 'IN': { c: 'INR', s: '₹' },
      'JP': { c: 'JPY', s: '¥' }, 'KR': { c: 'KRW', s: '₩' }, 'MX': { c: 'MXN', s: '$' },
      'MY': { c: 'MYR', s: 'RM' }, 'NZ': { c: 'NZD', s: '$' }, 'PE': { c: 'PEN', s: 'S/' },
      'PH': { c: 'PHP', s: '₱' }, 'PL': { c: 'PLN', s: 'zł' }, 'RU': { c: 'RUB', s: '₽' },
      'SA': { c: 'SAR', s: '﷼' }, 'SE': { c: 'SEK', s: 'kr' }, 'SG': { c: 'SGD', s: '$' },
      'TH': { c: 'THB', s: '฿' }, 'TR': { c: 'TRY', s: '₺' }, 'US': { c: 'USD', s: '$' },
      'VN': { c: 'VND', s: '₫' }, 'ZA': { c: 'ZAR', s: 'R' },
      'AT': { c: 'EUR', s: '€' }, 'BE': { c: 'EUR', s: '€' }, 'CY': { c: 'EUR', s: '€' },
      'EE': { c: 'EUR', s: '€' }, 'FI': { c: 'EUR', s: '€' }, 'FR': { c: 'EUR', s: '€' },
      'DE': { c: 'EUR', s: '€' }, 'GR': { c: 'EUR', s: '€' }, 'IE': { c: 'EUR', s: '€' },
      'IT': { c: 'EUR', s: '€' }, 'LV': { c: 'EUR', s: '€' }, 'LT': { c: 'EUR', s: '€' },
      'LU': { c: 'EUR', s: '€' }, 'MT': { c: 'EUR', s: '€' }, 'NL': { c: 'EUR', s: '€' },
      'PT': { c: 'EUR', s: '€' }, 'SK': { c: 'EUR', s: '€' }, 'SI': { c: 'EUR', s: '€' },
      'ES': { c: 'EUR', s: '€' }
    };

    this.init();
  }

  async init() {
    window.addEventListener('currency:changed', (e) => this.handleCurrencyChange(e.detail.currency, e.detail.symbol));
    
    // Fix the dropdown labels so they don't all show (BRL R$)
    this.fixDropdownLabels();
    
    // Check if there is a selected currency
    let selectedCurrency = localStorage.getItem('selected_currency');
    let selectedSymbol = localStorage.getItem('selected_symbol');
    
    // Auto-detect currency based on Shopify's native localization logic if Markets is enabled
    if (!selectedCurrency) {
      const countryInput = document.querySelector('input[name="country_code"]');
      if (countryInput) {
        const countryCode = countryInput.value;
        if (countryCode && countryCode !== 'BR') {
          const mapped = this.currencyMap[countryCode];
          if (mapped) {
            selectedCurrency = mapped.c;
            selectedSymbol = mapped.s;
          } else {
            // Fallback to USD for unknown non-BR countries
            selectedCurrency = 'USD';
            selectedSymbol = '$';
          }
          localStorage.setItem('selected_currency', selectedCurrency);
          localStorage.setItem('selected_symbol', selectedSymbol);
        }
      }
    }
    
    // Also update the dropdown visual state if it was loaded from another page
    this.updateDropdownVisual(selectedCurrency);
    
    if (selectedCurrency && selectedCurrency !== this.baseCurrency) {
      await this.loadRates();
      this.convertAllPrices(selectedCurrency, selectedSymbol);
    }
  }

  fixDropdownLabels() {
    const currencyMap = this.currencyMap;
    
    document.querySelectorAll('.localization-form__currency').forEach(el => {
      const link = el.closest('a');
      if (link) {
        const countryCode = link.dataset.value;
        const mapped = currencyMap[countryCode];
        if (mapped) {
          link.dataset.currency = mapped.c;
          link.dataset.symbol = mapped.s;
          // Display only the currency code and symbol (e.g. "USD ($)")
          el.innerHTML = `${mapped.c} (${mapped.s})`;
        }
      }
    });

    // Handle initial button state if no custom currency is selected but the default is a mapped country
    const selectedCurrency = localStorage.getItem('selected_currency');
    if (!selectedCurrency) {
      document.querySelectorAll('localization-form button span').forEach(span => {
         let text = span.innerText;
         if (text.includes('(BRL R$)')) {
           span.innerHTML = 'BRL (R$)';
         }
      });
    }
  }

  updateDropdownVisual(selectedCurrency) {
    if (!selectedCurrency || selectedCurrency === this.baseCurrency) return;
    const forms = document.querySelectorAll('localization-form');
    forms.forEach(form => {
      const link = form.querySelector(`a[data-currency="${selectedCurrency}"]`);
      if (link) {
        const currencyElement = link.querySelector('.localization-form__currency');
        const buttonSpan = form.querySelector('button span');
        if (currencyElement && buttonSpan) {
          buttonSpan.innerHTML = currencyElement.innerHTML;
        }
        
        form.querySelectorAll('li').forEach(li => li.classList.remove('active'));
        form.querySelectorAll('a').forEach(a => a.removeAttribute('aria-current'));
        
        link.closest('li').classList.add('active');
        link.setAttribute('aria-current', 'true');
      }
    });
  }

  async loadRates() {
    const cachedRates = localStorage.getItem(this.cacheKey);
    const cachedTime = localStorage.getItem(this.cacheTimeKey);
    
    if (cachedRates && cachedTime && (Date.now() - parseInt(cachedTime)) < this.cacheDuration) {
      this.rates = JSON.parse(cachedRates);
      return;
    }

    try {
      const response = await fetch(this.apiUrl);
      const data = await response.json();
      this.rates = data.rates;
      localStorage.setItem(this.cacheKey, JSON.stringify(this.rates));
      localStorage.setItem(this.cacheTimeKey, Date.now().toString());
    } catch (error) {
      console.error('Error fetching currency rates:', error);
    }
  }

  handleCurrencyChange(currency, symbol) {
    if (currency === this.baseCurrency) {
      this.restoreOriginalPrices();
      return;
    }
    this.loadRates().then(() => {
      this.convertAllPrices(currency, symbol);
    });
  }

  parseBRL(priceStr) {
    // Remove R$, spaces, HTML tags if any
    let clean = priceStr.replace(/<[^>]*>?/gm, '').replace(/[^\d,\.-]/g, '');
    // If it's a Brazilian format (e.g. 1.200,50), remove dots, replace comma with dot
    if (clean.includes(',') && clean.includes('.')) {
      clean = clean.replace(/\./g, '').replace(',', '.');
    } else if (clean.includes(',')) {
      clean = clean.replace(',', '.');
    }
    return parseFloat(clean);
  }

  formatPrice(amount, symbol, currencyCode) {
    try {
      return new Intl.NumberFormat(undefined, { 
        style: 'currency', 
        currency: currencyCode 
      }).format(amount);
    } catch (e) {
       // Fallback
       return `${symbol} ${amount.toFixed(2)}`;
    }
  }

  convertAllPrices(targetCurrency, symbol) {
    if (!this.rates || !this.rates[targetCurrency]) return;
    
    const rate = this.rates[targetCurrency];
    const priceElements = document.querySelectorAll('.price-item, .money, [data-js-product-price], .price, .cartCost, .total-price, del, ins, .unit_price');

    priceElements.forEach(el => {
      // Don't convert elements that are inside other elements we're converting
      if (el.querySelector('.price-item, .money, [data-js-product-price], .price, .cartCost, .total-price, del, ins, .unit_price')) return;
      
      if (!el.hasAttribute('data-original-price')) {
        const originalValue = this.parseBRL(el.innerText);
        if (isNaN(originalValue) || originalValue === 0) return;
        el.setAttribute('data-original-price', originalValue);
        el.setAttribute('data-original-html', el.innerHTML);
      }

      const originalPrice = parseFloat(el.getAttribute('data-original-price'));
      const newPrice = originalPrice * rate;
      
      el.innerHTML = this.formatPrice(newPrice, symbol, targetCurrency);
    });
  }

  restoreOriginalPrices() {
    const priceElements = document.querySelectorAll('[data-original-html]');
    priceElements.forEach(el => {
      el.innerHTML = el.getAttribute('data-original-html');
      el.removeAttribute('data-original-price');
      el.removeAttribute('data-original-html');
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.currencyConverter = new CurrencyConverter();
  
  // Create an observer to catch dynamically injected prices (like variants changing)
  const observer = new MutationObserver((mutations) => {
    let shouldConvert = false;
    for (let mutation of mutations) {
      if (mutation.addedNodes.length > 0 || mutation.type === 'characterData') {
         shouldConvert = true;
         break;
      }
    }
    if (shouldConvert) {
      const selectedCurrency = localStorage.getItem('selected_currency');
      const selectedSymbol = localStorage.getItem('selected_symbol');
      if (selectedCurrency && selectedCurrency !== 'BRL' && window.currencyConverter) {
        // Disconnect to avoid infinite loop when changing innerHTML
        observer.disconnect();
        
        // Timeout to let DOM updates finish
        setTimeout(() => {
          const priceElements = document.querySelectorAll('.price-item, .money, [data-js-product-price], .price, .cartCost, .total-price, del, ins, .unit_price');
          priceElements.forEach(el => {
              // If the text doesn't match the converted format (or we just force re-parse if it was overwritten)
              // Actually, if theme JS replaced the price, it removed the data-original-html attribute (because it replaces the whole element or its contents)
              // If the element still has data-original-price but its inner text is different, we should re-parse it
          });
          
          window.currencyConverter.convertAllPrices(selectedCurrency, selectedSymbol);
          observer.observe(document.body, { childList: true, subtree: true, characterData: true });
        }, 100);
      }
    }
  });
  
  observer.observe(document.body, { childList: true, subtree: true, characterData: true });
});
